import React, { Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useSearchParams,
} from "react-router-dom";
import { useSelector } from "react-redux";

import { AppDispatch, RootState } from "../redux/store";
import {
  Home,
  Login,
  ForgotPassword,
  Profile,
  ChangePassword,
  AddUser,
  UserList,
  Prior,
  AddCustomer,
  Customers,
  CustomerUsers,
  AddEditCustomerUsers,
  AgentList,
  AddEditAgentList,
  AgentRequestList,
  AddEditAgentRequest,
  CustomerOrder,
  CustomerOrderHistory,
  CustomerOrderHistoryEdit,
  DispatchBoardList,
  AgentListTabs,
  MasterAgentList,
  MasterAgentListTabs,
  MyRepList,
  AddEditSalesRep,
  BilledOrderHistory,
  XcelaratorApi,
  ViewBilledOrderHistory,
  AddEditXcelaratorApi,
  AddEditXcelaratorApiTabs,
  AddCustomerOfCustomer,
  CustomerRate,
  EmailNotificationPermissions,
  InvoiceGroups,
  InvoiceManagement,
  AddEditInvoiceGroups,
  AddEditOrderInvoice,
  SettingsAdditionalCharges,
  AddEditSettingsAdditionalCharges,
  AddEditAgentAdditionalCharges,
  AddEditCustomerAdditionalCharges,
  AddEditCustomerInvoiceGroups,
  FinalizedOrders,
  SavedReports,
  AddAgentApi,
  PackageTypeList,
  EmailTemplate,
  EmailConfig,
  AddNewEmailTemplate,
  AgentRate,
  UploadBulkQuote,
  UploadBulkOrder,
  BulkOrderProcess,
  BulkQuoteProcess,
  //TrackOrder
  CustomerAddress,
  CustomerAddressAdd,
  ProvidenceLocationList,
  AddEditLocation,
  AuditLog,
} from "../pages";
import { Loader } from "../components/loaders/loaders";
import Layout from "../components/layout/Layout";
import CreateReport from "../pages/report/CreateReport";
import TrackOrder from "../pages/track-order/TrackOrder";
import OrderTracking from "../pages/order-tracking";
import AgentApi from "../pages/api-management/AgentApi";
import Services from "../pages/service-config/services";
import VehicleType from "../pages/service-config/VehicleType";
import SavedQuotation from "../pages/Order-management/CustomerOrder/savedQuotation";
import QuickQuote from "../pages/Order-management/CustomerOrder/QuickQuote";
import CustomerItems from "../pages/customer-management/CustomerItems";
import AddEditCustomerItems from "../pages/customer-management/CustomerItems/AddEditCustomerItems";
import TradNonTrad from "../pages/Order-management/CustomerOrder/TradNonTrad";
import Scac from "../pages/service-config/Scac";
import { useDispatch } from "react-redux";
import { verifySessionToken } from "../utils/authUtils";
import {
  SALES_REP_ROLE,
  SALES_REP_ROUTE_PATHS,
} from "../utils/salesRepAccess";
import RegionListing from "../pages/service-config/Region/RegionListing";
import OrderStatusListing from "../pages/service-config/OrderStatus/OrderStatusListing";
import FscLocationListing from "../pages/service-config/FscLocation/FscLocationListing";
import OnwardServiceListing from "../pages/service-config/services/omniMoveUserServicePage";
import MasterZipCode from "../pages/service-config/MasterZipCode";


const AppRoutes: React.FC = () => {
  const { isAuthenticated, userRole, isUserOfOmniMove, customerLevel } =
    useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch<AppDispatch>();
  // const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Check for sessionId in URL and verify session token
   * This runs before route protection to handle cross-domain authentication
   */
  let sessionId = window?.location?.search?.split('=')[1] || '';

  // Only verify session if not already authenticated and sessionId exists
  if (sessionId) {
    verifySessionToken(sessionId, {
      dispatch,
      onSuccess: (data) => {
        // Clean URL - remove sessionId parameter after successful verification
        // window.history.replaceState({}, document.title, window.location.pathname);
        // window.location.reload()
      },
      onError: (error) => {
        console.error("Session verification failed:", error);
      },
    });

  }




  /**
   * Common Routes
   *
   * This array contains the public routes that are accessible to all users,
   * typically for actions such as signing in or recovering a password.
   */
  const publicRoutes = [
    { path: "/users/sign_in", element: <Login /> },
    { path: "/forgot-password", element: <ForgotPassword /> },
  ];

  /**
   * Protected Routes
   *
   * This array contains the protected routes that are accessible to auth users,
   * typically for actions such as User, Agent, Customer management.
   */
  const protectedRoutes = [
    { path: "/", element: <Home />, restricted: ["*"] },
    { path: "/user/profile", element: <Profile />, restricted: ["*"] },
    {
      path: "/email-notification-permissions",
      element: <EmailNotificationPermissions />,
      restricted: ["*"],
    },
    {
      path: "/users",
      element: <UserList />,
      restricted: [
        "superadmin",
        "admin",
        "customer",
        "manager",
        "customersuperadmin",
        "employee",
      ],
    },
    {
      path: "/users/add",
      element: <AddUser />,
      restricted: [
        "superadmin",
        "admin",
        "customer",
        "manager",
        "customersuperadmin",
      ],
    },
    {
      path: "/users/edit/:id",
      element: <AddUser />,
      restricted: [
        "superadmin",
        "admin",
        "customer",
        "manager",
        "customersuperadmin",
      ],
    },
    {
      path: "/change-password",
      element: <ChangePassword />,
      restricted: ["*"],
    },
    // Customer Management
    // {
    //   path: "/customers",
    //   element: <Customers />,
    //   restricted: isUserOfOmniMove
    //     ? ["superadmin", "admin", "manager", "customersuperadmin"]
    //     : ["admin", "manager", "customersuperadmin"] ||
    //       (customerLevel === "second" && [
    //         "admin",
    //         "manager",
    //         "customersuperadmin",
    //       ]) ||
    //       (customerLevel === "third" && ["customer", "employee","admin"]),
    // },
    {
      path: "/customers",
      element: <Customers />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "customersuperadmin"]
        : customerLevel === "second"
          ? ["admin", "manager", "customersuperadmin"]
          : customerLevel === "third"
            ? []
            : ["admin", "manager", "customersuperadmin"],
    },
    {
      path: "/customers/add",
      element: <AddCustomer />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "customersuperadmin"]
        : customerLevel === "second"
          ? ["admin", "manager", "customersuperadmin"]
          : customerLevel === "third"
            ? []
            : ["admin", "manager", "customersuperadmin"],
    },
    {
      path: "/customers/edit/:id",
      element: <AddCustomer />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "customersuperadmin"]
        : customerLevel === "second"
          ? ["admin", "manager", "customersuperadmin"]
          : customerLevel === "third"
            ? []
            : ["admin", "manager", "customersuperadmin"],
    },
    {
      path: "/customer-users",
      element: <CustomerUsers />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "second"
          ? ["admin", "manager", "customersuperadmin"]
          : customerLevel === "third"
            ? []
            : ["admin", "manager", "customersuperadmin"],
    },
    {
      path: "/customer-users/add",
      element: <AddEditCustomerUsers />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "second"
          ? ["admin", "manager", "customersuperadmin"]
          : customerLevel === "third"
            ? []
            : ["admin", "manager", "customersuperadmin"],
    },
    {
      path: "/customer-users/edit/:id",
      element: <AddEditCustomerUsers />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "second"
          ? ["admin", "manager", "customersuperadmin"]
          : customerLevel === "third"
            ? []
            : ["admin", "manager", "customersuperadmin"],
    },
    {
      path: "/customer-items",
      element: <CustomerItems />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "second"
          ? ["admin", "manager", "customersuperadmin"]
          : customerLevel === "third"
            ? []
            : ["admin", "manager", "customersuperadmin"],
    },
    {
      path: "/customer-items/add",
      element: <AddEditCustomerItems />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "second"
          ? ["admin", "manager", "customersuperadmin"]
          : customerLevel === "third"
            ? []
            : ["admin", "manager", "customersuperadmin"],
    },
    {
      path: "/customer-items/edit/:id",
      element: <AddEditCustomerItems />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "second"
          ? ["admin", "manager", "customersuperadmin"]
          : customerLevel === "third"
            ? []
            : ["admin", "manager", "customersuperadmin"],
    },
    {
      path: "/customers/customers/:id",
      element: <AddCustomerOfCustomer />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "second"
          ? ["admin", "manager", "customersuperadmin"]
          : customerLevel === "third"
            ? []
            : ["admin", "manager", "customersuperadmin"],
    },
    {
      path: "/customer/rate/:id",
      element: <CustomerRate />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "employee", "customersuperadmin"]
        : customerLevel === "third"
          ? ["customer", "employee"]
          : ["admin", "manager", "employee", "customersuperadmin"],
    },

    {
      path: "/customers/additional-charges/add",
      element: <AddEditCustomerAdditionalCharges />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "employee", "customersuperadmin"]
        : customerLevel === "third"
          ? ["customer", "employee"]
          : ["admin", "manager", "employee", "customersuperadmin"],
    },
    {
      path: "/customers/additional-charges/edit/:id",
      element: <AddEditCustomerAdditionalCharges />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "employee", "customersuperadmin"]
        : customerLevel === "third"
          ? ["customer", "employee"]
          : ["admin", "manager", "employee", "customersuperadmin"],
    },
    {
      path: "/customers/group-invoice-list/add",
      element: <AddEditCustomerInvoiceGroups />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "employee", "customersuperadmin"]
        : customerLevel === "second" || customerLevel === "third"
          ? []
          : ["admin", "manager", "employee", "customersuperadmin"],
    },
    {
      path: "/customers/group-invoice-list/edit/:id",
      element: <AddEditCustomerInvoiceGroups />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "employee", "customersuperadmin"]
        : customerLevel === "second"
          ? []
          : customerLevel === "third"
            ? ["customer", "employee"]
            : ["admin", "manager", "employee", "customersuperadmin"],
    },
    {
      path: "/customer-address-book",
      element: <CustomerAddress />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "first"
          ? ["admin", "manager", "employee", "customersuperadmin"]
          : [],
    },
    {
      path: "/customer-address-book/add",
      element: <CustomerAddressAdd />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "first"
          ? ["admin", "manager", "employee", "customersuperadmin"]
          : [],
    },
    {
      path: "/customer-address-book/edit/:id",
      element: <CustomerAddressAdd />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "first"
          ? ["admin", "manager", "employee", "customersuperadmin"]
          : [],
    },
    // Agent Management
    {
      path: "/agent-list",
      element: <AgentList />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "customersuperadmin"]
        : customerLevel === "second" || customerLevel === "third"
          ? []
          : ["admin", "manager", "customersuperadmin"],
    },
    // Agent Requests — DC (first-level) for create/edit/resend; OmniMove for approve/reject/view
    {
      path: "/agent-request",
      element: <AgentRequestList />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "customersuperadmin"]
        : customerLevel === "first"
          ? ["admin", "manager", "customersuperadmin"]
          : [],
    },
    {
      path: "/agent-request/add",
      element: <AddEditAgentRequest />,
      restricted:
        !isUserOfOmniMove && customerLevel === "first"
          ? ["admin", "manager", "customersuperadmin"]
          : [],
    },
    {
      path: "/agent-request/edit/:id",
      element: <AddEditAgentRequest />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "customersuperadmin"]
        : customerLevel === "first"
          ? ["admin", "manager", "customersuperadmin"]
          : [],
    },
    {
      path: "/agent-rates",
      element: <AgentRate />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "second" || customerLevel === "third"
          ? []
          : ["admin", "manager", "customersuperadmin"],
    },
    {
      path: "/agent-list/:agentId",
      element: <AgentListTabs />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "customersuperadmin"]
        : customerLevel === "second" || customerLevel === "third"
          ? []
          : ["admin", "manager", "customersuperadmin"],
    },
    {
      path: "/agent-list/additional-charges/add",
      element: <AddEditAgentAdditionalCharges />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "customersuperadmin"]
        : customerLevel === "second" || customerLevel === "third"
          ? []
          : ["admin", "manager", "customersuperadmin"],
    },
    {
      path: "/agent-list/additional-charges/edit/:id",
      element: <AddEditAgentAdditionalCharges />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "customersuperadmin"]
        : customerLevel === "second" || customerLevel === "third"
          ? []
          : ["admin", "manager", "customersuperadmin"],
    },
    {
      path: "/agent-list/add",
      element: <AddEditAgentList />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "customersuperadmin"]
        : customerLevel === "second" || customerLevel === "third"
          ? []
          : ["admin", "manager", "customersuperadmin"],
    },
    {
      path: "/agent-list/edit/:id",
      element: <AddEditAgentList />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "customersuperadmin"]
        : customerLevel === "second" || customerLevel === "third"
          ? []
          : ["admin", "manager", "customersuperadmin"],
    },
    {
      path: "/master-agent-list",
      element: <MasterAgentList />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "customersuperadmin"]
        : customerLevel === "second" || customerLevel === "third"
          ? []
          : ["admin", "manager", "customersuperadmin"],
    },
    {
      path: "/master-agent-list/:agentId",
      element: <MasterAgentListTabs />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "customersuperadmin"]
        : customerLevel === "second" || customerLevel === "third"
          ? []
          : ["admin", "manager", "customersuperadmin"],
    },
    // Rep Management — direct (first-level) customer super admin / admin / manager
    {
      path: "/rep-list",
      element: <MyRepList />,
      restricted:
        !isUserOfOmniMove && customerLevel === "first"
          ? ["admin", "manager", "customersuperadmin"]
          : [],
    },
    {
      path: "/rep-list/add",
      element: <AddEditSalesRep />,
      restricted:
        !isUserOfOmniMove && customerLevel === "first"
          ? ["admin", "manager", "customersuperadmin"]
          : [],
    },
    {
      path: "/rep-list/edit/:id",
      element: <AddEditSalesRep />,
      restricted:
        !isUserOfOmniMove && customerLevel === "first"
          ? ["admin", "manager", "customersuperadmin"]
          : [],
    },
    //order management
    {
      path: "/customer-order",
      element: <TradNonTrad />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "third"
          ? []
          : ["admin", "customer", "manager", "employee", "customersuperadmin"],
    },
    {
      path: "/quick-quote",
      element: <QuickQuote />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "third"
          ? []
          : ["admin", "customer", "manager", "employee", "customersuperadmin"],
    },
    {
      path: "/all-orders",
      element: <CustomerOrderHistory />,
      restricted: isUserOfOmniMove
        ? ["agent"]
        : [
          "admin",
          "customer",
          "manager",
          "employee",
          "agent",
          "customersuperadmin",
        ],
    },
    {
      path: "/active-order",
      element: <CustomerOrderHistory key={"active"} />,
      restricted: isUserOfOmniMove
        ? ["agent"]
        : [
          "admin",
          "customer",
          "manager",
          "employee",
          "agent",
          "customersuperadmin",
        ],
    },
    {
      path: "/delivered-order",
      element: <CustomerOrderHistory key={"delivered"} />,
      restricted: isUserOfOmniMove
        ? ["agent"]
        : [
          "admin",
          "customer",
          "manager",
          "employee",
          "agent",
          "customersuperadmin",
        ],
    },
    {
      path: "/finalized-order",
      element: <CustomerOrderHistory key={"finalized"} />,
      restricted: isUserOfOmniMove
        ? ["agent"]
        : [
          "admin",
          "customer",
          "manager",
          "employee",
          "agent",
          "customersuperadmin",
        ],
    },
    {
      path: "/cancelled-order",
      element: <CustomerOrderHistory key={"cancelled"} />,
      restricted: isUserOfOmniMove
        ? ["agent"]
        : [
          "admin",
          "customer",
          "manager",
          "employee",
          "agent",
          "customersuperadmin",
        ],
    },
    {
      path: "/invoiced-order",
      element: <CustomerOrderHistory key={"invoiced"} />,
      restricted: isUserOfOmniMove
        ? ["agent"]
        : [
          "admin",
          "customer",
          "manager",
          "employee",
          "agent",
          "customersuperadmin",
        ],
    },
    {
      path: "/all-orders/:id",
      element: <CustomerOrderHistoryEdit />,
      restricted: isUserOfOmniMove
        ? ["agent"]
        : [
          "admin",
          "customer",
          "manager",
          "employee",
          "agent",
          "customersuperadmin",
        ],
    },
    {
      path: "/invoiced-order/:id",
      element: <CustomerOrderHistoryEdit key={"invoiced"} />,
      restricted: isUserOfOmniMove
        ? []
        : [
          "admin",
          "customer",
          "manager",
          "employee",
          "agent",
          "customersuperadmin",
        ],
    },
    {
      path: "/active-order/:id",
      element: <CustomerOrderHistoryEdit key={"active"} />,
      restricted: isUserOfOmniMove
        ? []
        : [
          "admin",
          "customer",
          "manager",
          "employee",
          "agent",
          "customersuperadmin",
        ],
    },
    {
      path: "/finalized-order/:id",
      element: <CustomerOrderHistoryEdit key={"finalized"} />,
      restricted: isUserOfOmniMove
        ? []
        : [
          "admin",
          "customer",
          "manager",
          "employee",
          "agent",
          "customersuperadmin",
        ],
    },
    {
      path: "/cancelled-order/:id",
      element: <CustomerOrderHistoryEdit key={"cancelled"} />,
      restricted: isUserOfOmniMove
        ? []
        : [
          "admin",
          "customer",
          "manager",
          "employee",
          "agent",
          "customersuperadmin",
        ],
    },
    {
      path: "/delivered-order/:id",
      element: <CustomerOrderHistoryEdit key={"delivered"} />,
      restricted: isUserOfOmniMove
        ? []
        : [
          "admin",
          "customer",
          "manager",
          "employee",
          "agent",
          "customersuperadmin",
        ],
    },
    {
      path: "/dispatch-board-list",
      element: <DispatchBoardList />,
      restricted: isUserOfOmniMove
        ? []
        : ["admin", "customer", "manager", "employee", "customersuperadmin"],
    },
    {
      path: "/bulk-quote-import",
      element: <UploadBulkQuote />,
      restricted: isUserOfOmniMove
        ? []
        : ["admin", "customer", "manager", "employee"],
    },
    {
      path: "/bulk-quote-listing",
      element: <BulkQuoteProcess />,
      restricted:
        customerLevel === "first"
          ? ["customersuperadmin", "superadmin", "admin", "manager", "employee"]
          : [],
    },
    {
      path: "/bulk-order-import",
      element: <UploadBulkOrder />,
      restricted:
        customerLevel === "first"
          ? ["customersuperadmin", "superadmin", "admin", "manager", "employee"]
          : [],
    },
    {
      path: "/bulk-order-listing",
      element: <BulkOrderProcess />,
      restricted:
        customerLevel === "first"
          ? ["customersuperadmin", "superadmin", "admin", "manager", "employee"]
          : [],
    },
    {
      path: "/billed-order-history",
      element: <BilledOrderHistory />,
      restricted: isUserOfOmniMove
        ? [
          "superadmin",
          "admin",
          "manager",
          "employee",
          "agent",
          "customersuperadmin",
        ]
        : [],
    },
    {
      path: "/billed-order-history/view/:billedID",
      element: <ViewBilledOrderHistory />,
      restricted: isUserOfOmniMove
        ? [
          "superadmin",
          "admin",
          "manager",
          "employee",
          "agent",
          "customersuperadmin",
        ]
        : [],
    },
    // Api Management
    {
      path: "/Xcelarator-api",
      element: <XcelaratorApi />,
      restricted: isUserOfOmniMove
        ? ["admin", "manager", "employee", "customersuperadmin"]
        : customerLevel === "second"
          ? []
          : customerLevel === "first"
            ? ["superadmin", "admin", "manager", "customersuperadmin"]
            : [],
    },
    {
      path: "/Xcelarator-api/add",
      element: <AddEditXcelaratorApi />,
      restricted: isUserOfOmniMove
        ? ["admin", "manager", "employee", "customersuperadmin"]
        : customerLevel === "second"
          ? []
          : customerLevel === "first"
            ? ["superadmin", "admin", "manager", "customersuperadmin"]
            : [],
    },
    {
      path: "/Xcelarator-api/edit/:id",
      element: <AddEditXcelaratorApi />,
      restricted:
        customerLevel === "second"
          ? []
          : customerLevel === "first"
            ? ["superadmin", "admin", "manager", "customersuperadmin"]
            : [],
    },
    {
      path: "/Xcelarator-api/agent/:id",
      element: <AddEditXcelaratorApiTabs />,
      restricted:
        customerLevel === "second"
          ? []
          : customerLevel === "first"
            ? ["superadmin", "admin", "manager", "customersuperadmin"]
            : ["*"],
    },
    {
      path: "/agent-api/add",
      element: <AddAgentApi />,
      restricted: isUserOfOmniMove
        ? ["admin", "manager", "employee", "customersuperadmin"]
        : customerLevel === "second"
          ? []
          : customerLevel === "first"
            ? ["superadmin", "admin", "manager", "customersuperadmin"]
            : [],
    },
    {
      path: "/agent-api/edit/:id",
      element: <AddAgentApi />,
      restricted:
        customerLevel === "second"
          ? []
          : customerLevel === "first"
            ? ["superadmin", "admin", "manager", "customersuperadmin"]
            : [],
    },
    // Account Management
    {
      path: "/group-invoice-list",
      element: <InvoiceGroups />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "second" || customerLevel === "third"
          ? []
          : ["admin", "manager", "employee", "customersuperadmin"],
    },
    {
      path: "/group-invoice-list/add",
      element: <AddEditInvoiceGroups />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "second" || customerLevel === "third"
          ? []
          : ["admin", "manager", "employee", "customersuperadmin"],
    },
    {
      path: "/group-invoice-list/edit/:id",
      element: <AddEditInvoiceGroups />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "second"
          ? []
          : ["admin", "manager", "employee", "customersuperadmin"],
    },
    {
      path: "/invoice-list",
      element: <InvoiceManagement />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "second" || customerLevel === "third"
          ? []
          : ["admin", "manager", "employee", "customersuperadmin"],
    },
    {
      path: "/invoice-list/add",
      element: <AddEditOrderInvoice />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "employee", "customersuperadmin"]
        : customerLevel === "second"
          ? []
          : ["admin", "manager", "employee", "customersuperadmin"],
    },
    {
      path: "/invoice-list/edit/:id",
      element: <AddEditOrderInvoice />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "employee", "customersuperadmin"]
        : customerLevel === "second"
          ? []
          : ["admin", "manager", "employee", "customersuperadmin"],
    },
    {
      path: "/finalized-orders",
      element: <FinalizedOrders />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "second" || customerLevel === "third"
          ? []
          : ["admin", "manager", "employee", "customersuperadmin"],
    },
    //Agent api
    {
      path: "/agent-api",
      element: <AgentApi />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "second" || customerLevel === "third"
          ? []
          : ["admin", "manager", "customersuperadmin"],
    },
    //Reports
    {
      path: "/create-report",
      element: <CreateReport />,
      restricted:
        customerLevel === "first"
          ? ["admin", "manager", "employee", "customersuperadmin"]
          : [],
    },
    {
      path: "/saved-reports",
      element: <SavedReports />,
      restricted:
        customerLevel === "first"
          ? ["admin", "manager", "employee", "customersuperadmin"]
          : [],
    },
    // Audit Log
    {
      path: "/audit-log",
      element: <AuditLog />,
      restricted:
        customerLevel === "first"
          ? ["admin", "manager", "employee", "customersuperadmin"]
          : [],
    },
    // Settings
    {
      path: "/settings-additional-charges",
      element: <SettingsAdditionalCharges />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "employee", "customersuperadmin"]
        : ["admin", "manager", "employee", "customersuperadmin"],
    },
    {
      path: "/settings-additional-charges/add",
      element: <AddEditSettingsAdditionalCharges />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "employee", "customersuperadmin"]
        : ["admin", "manager", "employee", "customersuperadmin"],
    },
    {
      path: "/settings-additional-charges/edit/:id",
      element: <AddEditSettingsAdditionalCharges />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin", "manager", "employee", "customersuperadmin"]
        : ["admin", "manager", "employee", "customersuperadmin"],
    },
    {
      path: "/system-configuration/services",
      element: <Services />,
      restricted:
        customerLevel === "first" ? ["admin", "customersuperadmin"] : [],
    },
    {
      path: "/system-configuration/order-status",
      element: <OrderStatusListing />,
      restricted: isUserOfOmniMove
        ? ["superadmin", "admin"]
        : [],
    },
    {
      path: "/system-configuration/onward-service",
      element: <OnwardServiceListing />,
      restricted: isUserOfOmniMove
        ? ["superadmin"]
        : []
    },
    {
      path: "/system-configuration/package-type",
      element: <PackageTypeList />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "first"
          ? ["admin", "customersuperadmin"]
          : [],
    },
    {
      path: "/system-configuration/vehicle",
      element: <VehicleType />,
      restricted:
        customerLevel === "first" ? ["admin", "customersuperadmin"] : [],
    },
    {
      path: "/system-configuration/email-template",
      element: <EmailTemplate />,
      restricted:
        customerLevel === "first" ? ["admin", "customersuperadmin"] : [],
    },
    {
      path: "/system-configuration/emailConfig",
      element: <EmailConfig />,
      restricted:
        customerLevel === "first" ? ["admin", "customersuperadmin"] : [],
    },
    {
      path: "/system-configuration/region",
      element: <RegionListing />,
      restricted:
        customerLevel === "first" ? ["admin", "customersuperadmin"] : [],
    },
    {
      path: "/system-configuration/fsc-location",
      element: <FscLocationListing />,
      restricted:
        customerLevel === "first" ? ["admin", "customersuperadmin"] : [],
    },
    {
      path: "/system-configuration/add-email-template",
      element: <AddNewEmailTemplate />,
      restricted:
        customerLevel === "first" ? ["admin", "customersuperadmin"] : [],
    },
    {
      path: "/system-configuration/edit-email-template/:id",
      element: <AddNewEmailTemplate />,
      restricted:
        customerLevel === "first" ? ["admin", "customersuperadmin"] : [],
    },
    {
      path: "/saved-quotes",
      element: <SavedQuotation />,
      restricted: isUserOfOmniMove
        ? []
        : customerLevel === "third"
          ? []
          : ["admin", "customer", "manager", "employee", "customersuperadmin"],
    },
    {
      path: "/system-configuration/scac",
      element: <Scac />,
      restricted:
        customerLevel === "first" ? ["admin", "customersuperadmin"] : [],
    },
    {
      path: "/system-configuration/zip-code-list",
      element: <MasterZipCode />,
      restricted: isUserOfOmniMove ? ["superadmin", "admin"] : [],
    },
  ];

  /**
   * This array contains routes that are common and accessible under certain conditions,
   * such as setting a password using a token sent to the user.
   */
  const commonRoutes = [
    { path: "/user/set-password/:token", element: <Prior /> },
    { path: "/track-order/:id", element: <TrackOrder /> },
    { path: "/order/tracking", element: <OrderTracking /> },
  ];

  /**
   * Checks if a route is accessible based on the user's role.
   * @param route - The route object that contains the following properties:
   *   - path: The path of the route (e.g., "/dashboard").
   *   - element: The JSX element to render for this route.
   *   - restricted (optional): An array of roles that are allowed to access this route.
   * @returns A boolean value indicating whether the route is accessible for the current user.
   *   - true if the route is accessible.
   *   - false if the route is not accessible.
   */
  const canAccessRoute = (route: {
    path: string;
    element: JSX.Element;
    restricted?: string[];
  }): boolean => {
    // If there are no restrictions, the route is not accessible
    if (route.restricted === undefined || route.restricted.length === 0) {
      return false;
    }
    // salesRep is limited to its own allow-list (plus the universal "*" routes),
    // regardless of the route's restricted list. Scoped to salesRep only, so no
    // other role's access changes.
    if (userRole === SALES_REP_ROLE) {
      return (
        route.restricted.includes("*") ||
        SALES_REP_ROUTE_PATHS.has(route.path)
      );
    }
    // Handle routes that are universally accessible to all authenticated users
    if (route.restricted.includes("*")) {
      return true;
    }
    // Allow access if the user's role is in the restricted list
    return route.restricted.includes(userRole ?? "");
  };

  return (
    <Router>
      <Suspense fallback={<Loader />}>
        <Routes>
          {/* Public Routes */}
          {!isAuthenticated &&
            publicRoutes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ))}

          {/* Protected Routes */}
          {isAuthenticated && (
            <Route element={<Layout />}>
              {protectedRoutes
                .filter((route) => canAccessRoute(route))
                .map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={route.element}
                  />
                ))}
            </Route>
          )}

          {/* Common Routes */}
          {commonRoutes.map((route) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}

          {/* Default Redirect */}
          <Route
            path="*"
            element={
              <Navigate to={isAuthenticated ? "/" : "/users/sign_in"} replace />
            }
          />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default AppRoutes;
